import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bird, LogOut, User, Shield } from "lucide-react";

import { useCommunityAuth } from "../hooks/useCommunityAuth";
import { useFeed } from "../hooks/useFeed";
import {
  createPost,
  deletePost,
  toggleReaction,
  updatePost,
  moderatorHidePost,
} from "../lib/communityApi";
import { extractApiErrorMessage } from "../lib/format";

import CommunityLogin from "../components/community/CommunityLogin";
import TopicTabs from "../components/community/TopicTabs";
import PostComposer from "../components/community/PostComposer";
import PostCard from "../components/community/PostCard";
import ReplyThread from "../components/community/ReplyThread";
import KindReminderDialog from "../components/community/KindReminderDialog";
import ProfileDrawer from "../components/community/ProfileDrawer";

const KIND_REMINDER_KEY = "na_sg_community_kind_seen";

const readKindSeen = () => {
  try {
    return sessionStorage.getItem(KIND_REMINDER_KEY) === "1";
  } catch (err) {
    console.error("sessionStorage read failed:", err);
    return false;
  }
};
const writeKindSeen = () => {
  try {
    sessionStorage.setItem(KIND_REMINDER_KEY, "1");
  } catch (err) {
    console.error("sessionStorage write failed:", err);
  }
};

const CommunityHeader = ({ me, onOpenProfile, onSignOut }) => (
  <header className="bg-[#354556] text-white">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 border border-white/20">
          <Bird className="h-5 w-5 text-[#cfe7f8]" />
        </span>
        <span className="ht-display text-lg">Never Alone SG · Community</span>
      </Link>
      <div className="flex items-center gap-3">
        {me?.is_moderator && (
          <span
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#cfe7f8]/15 border border-[#cfe7f8]/30 text-[#cfe7f8] px-3 py-1 text-xs font-semibold"
            title="You can remove any post on this page."
          >
            <Shield className="h-3.5 w-3.5" />
            Moderator
          </span>
        )}
        <button
          onClick={onOpenProfile}
          className="ht-btn inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 px-3.5 py-1.5 text-sm"
        >
          <User className="h-4 w-4" />
          <span className="hidden sm:inline">{me?.first_name || "You"}</span>
        </button>
        <button
          onClick={onSignOut}
          className="ht-btn inline-flex items-center gap-2 rounded-full bg-[#cfe7f8] text-[#2c3e50] px-3.5 py-1.5 text-sm font-semibold"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    </div>
  </header>
);

const Community = () => {
  const { token, me, setMe, isAuthenticated, loading, signOut } =
    useCommunityAuth();
  const [topic, setTopic] = useState("all");
  const { topics, posts, setPosts, loading: feedLoading, reload } = useFeed(token, topic);

  const [submitting, setSubmitting] = useState(false);
  const [openRepliesFor, setOpenRepliesFor] = useState(null);
  const [kindOpen, setKindOpen] = useState(false);
  const [pendingPost, setPendingPost] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const submitPostNow = useCallback(
    async (payload) => {
      setSubmitting(true);
      try {
        const created = await createPost(token, payload);
        setPosts((prev) => [created, ...prev]);
        toast.success("Shared.");
        return true;
      } catch (err) {
        console.error("createPost failed:", err);
        toast.error(extractApiErrorMessage(err, "Could not share your post."));
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token, setPosts]
  );

  const handleSubmit = useCallback(
    async (payload) => {
      if (!readKindSeen()) {
        setPendingPost(payload);
        setKindOpen(true);
        return false;
      }
      return submitPostNow(payload);
    },
    [submitPostNow]
  );

  const handleKindAccept = useCallback(async () => {
    writeKindSeen();
    setKindOpen(false);
    if (pendingPost) {
      const p = pendingPost;
      setPendingPost(null);
      await submitPostNow(p);
    }
  }, [pendingPost, submitPostNow]);

  const handleReact = useCallback(
    async (postId) => {
      try {
        const res = await toggleReaction(token, postId, "heard");
        setPosts((prev) =>
          prev.map((p) => {
            if (p.id !== postId) return p;
            const reactions = { ...(p.reactions || {}), [res.kind]: res.count };
            const mine = new Set(p.my_reactions || []);
            if (res.active) mine.add(res.kind);
            else mine.delete(res.kind);
            return { ...p, reactions, my_reactions: Array.from(mine) };
          })
        );
      } catch (err) {
        console.error("toggleReaction failed:", err);
        toast.error("Could not save your reaction.");
      }
    },
    [token, setPosts]
  );

  const handleDelete = useCallback(
    async (postId) => {
      const yes = window.confirm("Delete this post? This cannot be undone.");
      if (!yes) return;
      try {
        await deletePost(token, postId);
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        toast.success("Post deleted.");
      } catch (err) {
        console.error("deletePost failed:", err);
        toast.error("Could not delete the post.");
      }
    },
    [token, setPosts]
  );

  const handleModeratorHide = useCallback(
    async (postId) => {
      const reason = window.prompt(
        "Hide this post as moderator. Optional reason (private, visible only in /admin):",
        ""
      );
      if (reason === null) return;
      try {
        await moderatorHidePost(token, postId, reason || undefined);
        // Remove from current feed view; the post is preserved in DB and can
        // be unhidden from the admin dashboard.
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        toast.success("Post hidden (moderator). Reversible from /admin.");
      } catch (err) {
        console.error("moderatorHidePost failed:", err);
        toast.error("Could not hide the post.");
      }
    },
    [token, setPosts]
  );

  const handleSaveEdit = useCallback(
    async (postId, body) => {
      try {
        const updated = await updatePost(token, postId, body);
        setPosts((prev) => prev.map((p) => (p.id === postId ? updated : p)));
        toast.success("Post updated.");
        return true;
      } catch (err) {
        console.error("updatePost failed:", err);
        toast.error(extractApiErrorMessage(err, "Could not save your edit."));
        return false;
      }
    },
    [token, setPosts]
  );

  if (!isAuthenticated) {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center text-[#5a6b7d]">
          Loading…
        </div>
      );
    }
    return <CommunityLogin />;
  }

  return (
    <div className="min-h-screen bg-[#f6f9fc]">
      <CommunityHeader
        me={me}
        onOpenProfile={() => setProfileOpen(true)}
        onSignOut={signOut}
      />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[#4a90c2] font-semibold uppercase tracking-[0.18em] text-xs ht-body">
              Community feed
            </p>
            <h1 className="mt-1 ht-display text-2xl sm:text-3xl text-[#2c3e50]">
              Hi {me?.first_name || "friend"}. Take a seat.
            </h1>
          </div>
          <button
            onClick={reload}
            className="text-sm text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
          >
            Refresh
          </button>
        </div>

        <div className="mt-6">
          <TopicTabs topics={topics} value={topic} onChange={setTopic} />
        </div>

        <div className="mt-6">
          <PostComposer
            topics={topics}
            defaultTopic={topic}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        </div>

        <section className="mt-8 space-y-4">
          {feedLoading && (
            <p className="text-sm text-[#7e8a96] text-center py-6">
              Loading the feed…
            </p>
          )}
          {!feedLoading && posts.length === 0 && (
            <div className="rounded-2xl bg-white border border-[#e6eef5] p-8 text-center shadow-sm">
              <p className="ht-display text-lg text-[#2c3e50]">
                It’s quiet here.
              </p>
              <p className="mt-2 ht-body text-sm text-[#5a6b7d]">
                Be the first to gently say something — a small thought, a hard
                day, a tiny win. Whatever feels true.
              </p>
            </div>
          )}
          {!feedLoading &&
            posts.map((p) => (
              <div key={p.id}>
                <PostCard
                  post={p}
                  topics={topics}
                  isMine={p?.author?.id === me?.id}
                  isModerator={Boolean(me?.is_moderator)}
                  onReact={() => handleReact(p.id)}
                  onOpenReplies={() =>
                    setOpenRepliesFor((cur) => (cur === p.id ? null : p.id))
                  }
                  onDelete={() => handleDelete(p.id)}
                  onModeratorHide={() => handleModeratorHide(p.id)}
                  onSaveEdit={handleSaveEdit}
                />
                {openRepliesFor === p.id && (
                  <ReplyThread
                    token={token}
                    post={p}
                    onClose={() => setOpenRepliesFor(null)}
                    onCountChange={(n) =>
                      setPosts((prev) =>
                        prev.map((x) =>
                          x.id === p.id ? { ...x, reply_count: n } : x
                        )
                      )
                    }
                  />
                )}
              </div>
            ))}
        </section>
      </main>

      <KindReminderDialog
        open={kindOpen}
        onAccept={handleKindAccept}
        onClose={() => {
          setKindOpen(false);
          setPendingPost(null);
        }}
      />

      <ProfileDrawer
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        token={token}
        me={me}
        onUpdated={(updated) => setMe(updated)}
      />
    </div>
  );
};

export default Community;
